/** Pure month/stat math for the Reports page (P50).
 *
 * Mirrors the operator's reference dashboard: the month's net P&L, win
 * rate, and the max drawdown / max run-up of the cumulative day-by-day
 * P&L curve, with the day each extreme occurred.
 *
 * ⚠️ These were computed PER EXIT STYLE until 2026-08-16 — the real trade
 * beside four simulated ones. The shadow machinery was removed, so there
 * is one series now: what actually happened.
 */

import type { TradeComparison } from "@/lib/reportsApi";

export interface StyleStats {
  /** What the trades made BEFORE charges. */
  grossPnl: number;
  /** Brokerage, STT and the rest, summed over the same trades. */
  charges: number;
  /** grossPnl - charges. Until 2026-09-23 this was the gross figure under a
   * "Net" label, so the page overstated every month it showed (EMA September:
   * 1,37,362 shown against 1,31,962 actually earned). */
  netPnl: number;
  closed: number;
  wins: number;
  /** Percent 0-100, null when no closed trades. */
  winRate: number | null;
  /** <= 0; 0 means "no drawdown". */
  maxDrawdown: number;
  maxDrawdownDate: string | null;
  /** >= 0; 0 means "no run-up". */
  maxRunUp: number;
  maxRunUpDate: string | null;
}

/** "2026-07" for a Date, in IST. */
export function monthKeyOf(date: Date): string {
  return date.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);
}

/** Parse a "YYYY-MM" month key into numbers. */
function parseMonthKey(key: string): { year: number; month: number } {
  return { year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)) };
}

/** Last `n` month keys, newest first, ending at the current IST month. */
export function lastMonthKeys(n: number, today: Date): string[] {
  const { year, month } = parseMonthKey(monthKeyOf(today));
  const keys: string[] = [];
  for (let i = 0; i < n; i++) {
    const m = month - i;
    const y = year + Math.floor((m - 1) / 12);
    const mm = ((m - 1 + 120) % 12) + 1;
    keys.push(`${String(y)}-${String(mm).padStart(2, "0")}`);
  }
  return keys;
}

/** Inclusive from/to dates covering one month key ("2026-07" → 01..31). */
export function monthRange(key: string): { from: string; to: string } {
  const { year, month } = parseMonthKey(key);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { from: `${key}-01`, to: `${key}-${String(lastDay).padStart(2, "0")}` };
}

export function monthLabel(key: string): string {
  const { year, month } = parseMonthKey(key);
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${names[month - 1] ?? "?"} ${String(year)}`;
}

/** IST calendar day of an ISO timestamp (groups trades into day buckets). */
export function istDayOf(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

/** Trading days present in the trade list (by the day P&L landed, IST). */
export function tradingDays(trades: TradeComparison[]): number {
  return new Set(trades.map((t) => istDayOf(t.report_at))).size;
}

export function computeStyleStats(trades: TradeComparison[]): StyleStats {
  const dailyPnl = new Map<string, number>();
  let closed = 0;
  let wins = 0;
  let grossPnl = 0;
  let charges = 0;
  for (const trade of trades) {
    const pnl = trade.live.pnl ?? null;
    if (pnl === null) continue;
    closed++;
    if (pnl > 0) wins++;
    grossPnl += pnl;
    charges += trade.live.charges ?? 0;
    // The curve is drawn NET, so a drawdown is the one the account felt.
    const day = istDayOf(trade.report_at);
    dailyPnl.set(day, (dailyPnl.get(day) ?? 0) + pnl - (trade.live.charges ?? 0));
  }
  const peaks = cumulativePeaks(dailyPnl);
  return {
    grossPnl,
    charges,
    netPnl: grossPnl - charges,
    closed,
    wins,
    winRate: closed > 0 ? Math.round((wins / closed) * 100) : null,
    ...peaks,
  };
}

/** Max drawdown / run-up over the cumulative curve of day P&Ls, sorted by
 * day — the reference dashboard's calcMonthPeaks. */
function cumulativePeaks(dailyPnl: Map<string, number>): {
  maxDrawdown: number;
  maxDrawdownDate: string | null;
  maxRunUp: number;
  maxRunUpDate: string | null;
} {
  const days = [...dailyPnl.entries()].sort(([a], [b]) => (a < b ? -1 : 1));
  let cumulative = 0;
  let peak = 0;
  let trough = 0;
  let maxDrawdown = 0;
  let maxRunUp = 0;
  let drawdownDate: string | null = null;
  let runUpDate: string | null = null;
  for (const [day, pnl] of days) {
    cumulative += pnl;
    if (cumulative > peak) peak = cumulative;
    if (cumulative < trough) trough = cumulative;
    const drawdown = peak - cumulative;
    const runUp = cumulative - trough;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
      drawdownDate = day;
    }
    if (runUp > maxRunUp) {
      maxRunUp = runUp;
      runUpDate = day;
    }
  }
  return {
    maxDrawdown: maxDrawdown > 0 ? -maxDrawdown : 0,
    maxDrawdownDate: drawdownDate,
    maxRunUp,
    maxRunUpDate: runUpDate,
  };
}

/** All weekdays (Mon-Fri) of a month key — date-filter options for the
 * hub, where per-strategy trade days aren't known up front. */
export function monthWeekdays(key: string): string[] {
  const { from, to } = monthRange(key);
  const days: string[] = [];
  const cursor = new Date(`${from}T00:00:00+05:30`);
  const end = new Date(`${to}T00:00:00+05:30`);
  while (cursor <= end) {
    const dow = cursor.getUTCDay();
    if (dow !== 0 && dow !== 6) {
      days.push(cursor.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }));
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}
